/**
 * CONMEMORACIÓN — el 25 de Agosto la marca se pone celeste.
 *
 * (25/08/2026, pedido del user) Por la Declaratoria de la Independencia, el logo de Imperium
 * y la aureola de Gustavo viran al celeste de la bandera y les aparece la banderita al lado.
 * Al pasar el mouse por el logo se explica por qué.
 *
 * ⛔ SE APAGA SOLO AL ENTRAR EL 26 DE AGOSTO, HORA URUGUAY. Y esa última parte es todo el
 * problema: si la fecha se leyera del reloj del navegador, un agente en España lo vería
 * apagarse cuatro horas antes que uno en Montevideo, y uno en México cuatro después. Por eso
 * la fecha se calcula SIEMPRE contra `America/Montevideo`, sin importar dónde esté quien mira.
 *
 * ⛔ HOY ESTÁ APAGADA A MANO: ver `CONMEMORACION_ACTIVA` más abajo (25/08/2026, pedido del
 * user). La lógica de fecha sigue entera debajo del interruptor; nada de esto se ve
 * mientras la constante esté en `false`.
 */

/** El celeste de la bandera. Es el mismo tono para el logo y para la aureola. */
export const CELESTE_URUGUAY = '#75AADB';

/**
 * La escala celeste completa, con los mismos nombres que la dorada de la paleta.
 *
 * Existe para poder pisar `paleta.brand` de una sola vez donde haga falta —el sidebar la
 * usa en seis tonos distintos entre el item activo, el hover, el degradado y la sombra—.
 * Cambiarlos uno por uno es la forma segura de que alguno quede dorado y el conjunto se
 * vea roto. (24/08/2026)
 *
 * `goldenDark` es el que se usa en modo CLARO, así que va francamente más oscuro: sobre un
 * sidebar blanco el celeste de la bandera se desvanece.
 */
export const ESCALA_CELESTE = {
  golden:       CELESTE_URUGUAY,
  goldenLight:  '#A8CDEA',
  goldenMid:    '#4A86C4',
  goldenDark:   '#2F6FA8',
  goldenSubtle: 'rgba(117,170,219,0.12)',
} as const;

/**
 * El logo es un PNG DORADO y no hay versión celeste del asset.
 *
 * Virarlo con filtros CSS evita sumar una imagen nueva que después hay que mantener
 * sincronizada con el logo real cada vez que la marca cambie. Los valores salieron de
 * comparar variantes sobre el PNG de verdad, no de calcular la rotación de tono en abstracto:
 * el dorado del logo no es un color plano y el resultado teórico se veía apagado.
 */
export const FILTRO_CELESTE = 'hue-rotate(178deg) saturate(1.15) brightness(1.05)';

/**
 * La versión para fondos CLAROS — la barra del CRM en modo claro es blanca.
 *
 * (24/08/2026) El filtro de arriba sube el brillo porque está pensado para leerse sobre
 * negro; sobre blanco ese mismo celeste se desvanece y el logo casi desaparece. Acá el
 * brillo baja y la saturación sube, para que el celeste se sostenga contra el blanco sin
 * dejar de ser el mismo color.
 */
export const FILTRO_CELESTE_SOBRE_CLARO = 'hue-rotate(178deg) saturate(1.7) brightness(0.72)';

/** El título del tooltip del logo. */
export const TEXTO_CONMEMORACION = 'En conmemoración a la Independencia del Uruguay';

/** La bajada del tooltip: la fecha concreta que se está recordando. */
export const DETALLE_CONMEMORACION = '25 de Agosto de 1825 — Declaratoria de la Independencia';

/** La banderita que va al lado del logo. Emoji: cero peso y se ve nativa en cada sistema. */
export const BANDERA = '🇺🇾';

/**
 * ⛔ INTERRUPTOR MAESTRO — APAGADO (25/08/2026, pedido del user: "desactivá ya el modo color").
 *
 * Gana sobre TODO: sobre la fecha, sobre el atajo de local y sobre `?independencia=1`. Con
 * esto en `false` el CRM y el sitio público vuelven al dorado de siempre y no queda ni la
 * banderita ni el panel de la declaratoria, sin tocar ningún componente — todos preguntan
 * por `conmemoracionDeHoy()` / `conmemoracionCrm()`, que devuelven `null`.
 *
 * Para volver a encenderlo el año que viene: poner `true`. La lógica de fecha quedó intacta
 * abajo, así que se enciende solo el 25 y se apaga solo el 26, hora de Montevideo.
 */
export const CONMEMORACION_ACTIVA = false;

/** Día y mes en los que la conmemoración está activa. */
const DIA = 25;
const MES = 8;

/**
 * La fecha de HOY en Uruguay, sin importar el huso del navegador.
 *
 * `en-CA` da el formato `YYYY-MM-DD`, que se parte sin ambigüedad — a diferencia de
 * `toLocaleDateString()` a secas, que según el locale devuelve `25/8/2026` o `8/25/2026` y
 * hace que el día y el mes se confundan justo en las fechas donde los dos son válidos.
 */
export function fechaEnUruguay(ahora: Date = new Date()): { anio: number; mes: number; dia: number } {
  const iso = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Montevideo',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(ahora);
  const [anio, mes, dia] = iso.split('-').map(Number);
  return { anio, mes, dia };
}

/**
 * ¿Estamos en el 25 de Agosto en Uruguay?
 *
 * Se compara contra el día tal como se vive allá, así que arranca a las 00:00 de Montevideo
 * y termina a las 23:59:59 de Montevideo, para todos los que miren la pantalla.
 */
export function esDiaDeLaIndependencia(ahora: Date = new Date()): boolean {
  const { mes, dia } = fechaEnUruguay(ahora);
  return mes === MES && dia === DIA;
}

/**
 * El override de la URL: `?independencia=1` lo enciende, `?independencia=0` lo apaga.
 *
 * Hace falta porque el efecto dura un día y solo se enciende a la medianoche de Montevideo:
 * sin esto, la única forma de revisar cómo quedó es esperar a que pase, y para entonces ya
 * no se puede corregir nada. Mismo mecanismo que `?brand=` en `theme/brand.ts`.
 *
 * ⛔ NO se persiste (nada de localStorage): se va solo al recargar sin el parámetro. Un
 * override pineado en el navegador dejaría a alguien viendo la marca celeste en noviembre
 * sin entender por qué.
 *
 * Devuelve `null` cuando no hay parámetro, para poder distinguir "no dijo nada" de "dijo
 * que no". Con un booleano suelto, `?independencia=0` no tendría forma de apagar nada.
 */
function overrideDeUrl(): boolean | null {
  try {
    const v = new URLSearchParams(window.location.search).get('independencia');
    if (v === '1') return true;
    if (v === '0') return false;
    return null;
  } catch {
    return null;
  }
}

/**
 * ⛔ EN LOCAL ESTÁ SIEMPRE ENCENDIDO. (pedido del user, 24/08/2026)
 *
 * Trabajando en desarrollo el efecto es invisible 364 días al año, y el único día que se
 * puede ver es el día en que ya es tarde para arreglarlo. Así que en `npm run dev` se
 * muestra siempre, y la fecha manda únicamente en producción — que es donde importa.
 *
 * Para ver el estado normal (dorado) sin salir de local: `?independencia=0`.
 */
const esDesarrollo = (): boolean => {
  try { return !!import.meta.env?.DEV; } catch { return false; }
};

/**
 * Lo que hay que aplicarle al logo hoy. `null` = dejarlo como está.
 *
 * Devolver `null` en vez de un objeto con todo apagado es a propósito: quien lo usa escribe
 * `conmemoracion && <...>` y no puede olvidarse de mirar un booleano suelto.
 */
export function conmemoracionDeHoy(
  ahora: Date = new Date(),
  /**
   * El entorno entra como parámetro y no se lee adentro a propósito.
   *
   * Los tests corren con `import.meta.env.DEV === true`, así que el atajo de local tapaba
   * la lógica de fecha y ningún test podía probar lo que va a pasar EN PRODUCCIÓN — que es
   * justo lo único que importa acá. Pasándolo se prueban los dos mundos.
   */
  enDesarrollo: boolean = esDesarrollo(),
  /**
   * El interruptor maestro, inyectable por el mismo motivo que `enDesarrollo`: apagado en
   * producción, los tests igual tienen que poder probar la máquina que se va a encender el
   * año que viene.
   */
  habilitada: boolean = CONMEMORACION_ACTIVA,
): {
  filtro: string;
  color: string;
  filtroSobreClaro: string;
  bandera: string;
  titulo: string;
  detalle: string;
} | null {
  // ⛔ El interruptor maestro va PRIMERO: apagado, no hay URL ni local ni fecha que lo encienda.
  if (!habilitada) return null;
  // Precedencia: lo que diga la URL gana; después, en local siempre se ve; y en producción
  // decide la fecha en Montevideo.
  const forzado = overrideDeUrl();
  const activa = forzado ?? (enDesarrollo || esDiaDeLaIndependencia(ahora));
  if (!activa) return null;
  return DATOS_CONMEMORACION;
}

/**
 * Los datos de la conmemoración, SIN preguntar por la fecha.
 *
 * Existe para quien YA sabe que está encendida y solo necesita el contenido —el CRM lo
 * deduce del viraje del acento en `applyCrmAccent`—. Volver a resolver la fecha afuera es
 * exactamente lo que rompió la conmemoración una vez: la pantalla quedaba celeste y la
 * banderita no aparecía, porque las dos respuestas no coincidían.
 */
export const DATOS_CONMEMORACION = {
  filtro: FILTRO_CELESTE,
  filtroSobreClaro: FILTRO_CELESTE_SOBRE_CLARO,
  color: CELESTE_URUGUAY,
  bandera: BANDERA,
  titulo: TEXTO_CONMEMORACION,
  detalle: DETALLE_CONMEMORACION,
};
